
export interface SuccessEnvelope<T> {
  data: T;
  meta?: PaginationMeta | Record<string, never>;
  errors: [];
}

export interface ErrorDetail {
  code: ErrorCode;
  field?: string;
  message: string;
  details?: Record<string, unknown>;
}

export interface ErrorEnvelope {
  data: null;
  errors: ErrorDetail[];
}

export interface PaginationMeta {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

export type ErrorCode =
  | 'VALIDATION_ERROR'
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'CONFLICT'
  | 'PAYLOAD_TOO_LARGE'
  | 'INTERNAL_ERROR'
  | 'SERVICE_UNAVAILABLE';


export type PipelineRunState =
  | 'pending'
  | 'running'
  | 'completed'
  | 'failed'
  | 'paused_for_approval';

export interface PipelineRunReadDto {
  id: string;
  project_id: string;
  workflow_id: string;
  triggered_by_user_id: string;
  state: PipelineRunState;
  branch: string;
  inputs: Record<string, unknown>;
  correlation_id: string;
  started_at: string;
  finished_at: string | null;
  workspace_reclaimed_at?: string | null;
  workspace_cleanup_due_at?: string | null;
  signals?: RunSignalSummary;
}

export interface PipelineRunListItemDto extends PipelineRunReadDto {
  workflow_name: string;
  workflow_definition_hash: string;
  latest_stage: string | null;
}

export interface PipelineRunListQuery {
  status?: 'active' | 'completed' | 'failed';
  workflow_definition_hash?: string;
  started_from?: string;
  started_to?: string;
  project_id?: string;
  page?: number;
  per_page?: number;
  sort?: 'started_at_desc' | 'started_at_asc';
}


export type StageState =
  | 'pending'
  | 'running'
  | 'completed'
  | 'failed'
  | 'paused_for_approval'
  | 'skipped';

export type StageOutcome = 'pending' | 'running' | 'succeeded' | 'failed' | null;

export interface StageSummaryDto {
  stage_id: string;
  agent_id: string;
  state: StageState;
  outcome: StageOutcome;
  started_at: string | null;
  completed_at: string | null;
  failure_reason: string | null;
  skip_reason?: string | null;
  reworks_requested?: number;
  max_attempt?: number;
  reworks_total?: number;
  rework_cap?: number;
  self_verify_loops?: number;
  self_verify_total?: number;
  self_verify_cap?: number;
  cap_reached?: boolean;
  failed_sub_stage?: string;
  artefact_count: number;
}

export interface StageListResponseDto {
  pipeline_run_id: string;
  stages: ReadonlyArray<StageSummaryDto>;
}


export interface EventDto {
  event_id: string;
  correlation_id: string;
  pipeline_run_id?: string | null;
  service: string;
  event_type: string;
  actor: string;
  actor_role?: string | null;
  actor_email?: string | null;
  message?: string | null;
  emitted_at: string;
  details: Record<string, unknown>;
}

export interface EventSearchQuery {
  correlation_id?: string;
  subject?: string;
  pipeline_run_id?: string;
  service?: string;
  event_type?: string;
  actor?: string;
  emitted_from?: string;
  emitted_to?: string;
  page?: number;
  per_page?: number;
}

export interface EventSearchResponseDto {
  events: ReadonlyArray<EventDto>;
}


export type PipelineRunEventFrame =
  | { kind: 'stage.started'; stage_id: string; emitted_at: string; cursor?: string }
  | { kind: 'stage.completed'; stage_id: string; emitted_at: string; cursor?: string }
  | { kind: 'stage.failed'; stage_id: string; failure_reason: string; emitted_at: string; cursor?: string }
  | { kind: 'stage.progress'; stage_id: string; progress_pct: number; step: string; latency_ms: number; emitted_at: string; cursor?: string }
  | { kind: 'run.state_changed'; state: PipelineRunState; emitted_at: string; cursor?: string }
  | { kind: 'back_edge.fired'; from_stage: string; to_stage: string; attempt_number: number; max_attempts?: number; emitted_at: string; cursor?: string }
  | {
      kind: 'agent.activity';
      stage_id: string;
      invocation_key: string;
      turn: number;
      seq: number;
      phase: 'tool_started' | 'tool_args' | 'turn_completed';
      tool_names: ReadonlyArray<string>;
      summary: string;
      emitted_at: string;
    }
  | { kind: 'event'; event: EventDto; cursor?: string };

export interface RunTraceRow {
  stage_id: string;
  invocation_key: string;
  seq: number;
  ts: string;
  turn: number;
  type: string;
  payload: Record<string, unknown>;
  role?: string;
  model?: string;
}

export type RunTraceStatus = 'ok' | 'reclaimed' | 'tracing_disabled' | 'no_trace_yet';

export interface RunTraceResponse {
  status: RunTraceStatus;
  rows: ReadonlyArray<RunTraceRow>;
  invocations: ReadonlyArray<{
    stage_id: string;
    invocation_key: string;
    role?: string;
    model?: string;
    started_at?: string;
    events: number;
  }>;
  partial: boolean;
  next_offset?: number;
}

export interface StopRunResponse {
  pipeline_run_id: string;
  state: PipelineRunState;
  stopped: boolean;
  already_terminal?: boolean;
  aborted_in_flight?: boolean;
  stopped_at?: string;
}


export type AgentRole =
  | 'explorer'
  | 'architect'
  | 'coder'
  | 'reviewer'
  | 'tester'
  | 'documenter';

export interface ArtefactProvenance {
  produced_by_role: AgentRole;
  produced_by_agent_id: string;
  produced_at: string;
  pipeline_run_id: string;
  parent_artefact_ref: string | null;
}

export interface ArtefactHandle {
  ref: string;
  inline_bytes: number;
  full_bytes: number;
  preview_truncated: boolean;
}


export type FindingType =
  | 'file_discovery'
  | 'pattern_match'
  | 'call_chain_node'
  | 'entity'
  | 'module'
  | 'convention'
  | 'gap'
  | 'risk';

export interface ExplorerFinding {
  file_path: string;
  line_number: number;
  finding_type: FindingType;
  summary: string;
}

export type ExplorerAnalysisMode = 'codebase' | 'requirement' | 'codebase+requirement';

export interface ExplorerOutput {
  kind: 'explorer';
  summary: string;
  analysis_mode?: ExplorerAnalysisMode;
  findings: ReadonlyArray<ExplorerFinding>;
  files_inspected: number;
}

export interface ArchitectTask {
  task_id: string;
  title: string;
  depends_on: ReadonlyArray<string>;
  acceptance_criteria: ReadonlyArray<string>;
  target_files: ReadonlyArray<string>;
}

export interface InterfaceContract {
  contract_id: string;
  name: string;
  provider_task_id: string;
  consumer_task_ids: ReadonlyArray<string>;
  interface_type: string;
  specification: Record<string, ReadonlyArray<string>>;
}

export interface ArchitectOutput {
  kind: 'architect';
  tasks: ReadonlyArray<ArchitectTask>;
  interfaces: ReadonlyArray<InterfaceContract>;
  execution_schedule: ReadonlyArray<string>;
  analysis_summary: string;
  acceptance_criteria: ReadonlyArray<string>;
  plan_markdown: string;
}

export type DiffLineKind = 'context' | 'add' | 'del';

export interface DiffLine {
  kind: DiffLineKind;
  old_line: number | null;
  new_line: number | null;
  text: string;
  inline_comment?: string;
}

export interface CoderFile {
  file_path: string;
  diff: ReadonlyArray<DiffLine>;
  handle?: ArtefactHandle;
}

export interface SelfVerifyTestCase {
  test_name: string;
  passed: boolean;
}

export interface SelfVerifyTestResults {
  tests_total: number;
  tests_passed: number;
  tests_failed: number;
  failing_cases: ReadonlyArray<string>;
  cases: ReadonlyArray<SelfVerifyTestCase>;
  evidence_available: boolean;
}

export interface StructuredFinding {
  what: string;
  why: string;
  evidence: string;
  expected?: string;
  actual?: string;
  suggested_fix: string;
  file: string;
  ac_link: string;
  severity: string;
  blocking: boolean;
}

export interface StructuredGateAnalysis {
  summary: string;
  findings: ReadonlyArray<StructuredFinding>;
  fix_files: ReadonlyArray<string>;
  source: 'llm' | 'fallback_raw';
}

export interface CoderSelfVerification {
  ran: boolean;
  passed: boolean;
  advisory_unverified: boolean;
  loops_run: number;
  max_loops?: number;
  total_loops_run?: number;
  final_gate_passed: boolean;
  final_gate_reason: string;
  final_test_results: SelfVerifyTestResults;
  structured_analysis?: StructuredGateAnalysis | null;
}

export interface CoderOutput {
  kind: 'coder';
  files: ReadonlyArray<CoderFile>;
  self_verification?: CoderSelfVerification | null;
  verification_gate?: VerificationGate | null;
}

export type IssueSeverity = 'Critical' | 'High' | 'Medium' | 'Low';

export type ReviewerIssueStatus = 'fixed' | 'open' | 'new';

export interface ReviewerIssue {
  issue_id: string;
  category: string;
  catalogue_entry_id: string;
  severity: IssueSeverity;
  file_path: string;
  line_start: number;
  line_end: number;
  impact: string;
  reproduction_steps: ReadonlyArray<string>;
  suggested_fix: string;
  proof_test_case: string | null;
  status?: ReviewerIssueStatus;
}

export interface ReviewerSummary {
  total_issues: number;
  by_severity: Record<string, number>;
  by_category: Record<string, number>;
}

export interface ReviewerOutput {
  kind: 'reviewer';
  verdict: string | null;
  issues: ReadonlyArray<ReviewerIssue>;
  summary: ReviewerSummary | null;
  ledger?: { resolved_count: number; remaining_count: number } | null;
}

export type DeterminismClassification = 'deterministic' | 'flaky' | 'nondeterministic';
export type TestMark = 'pass' | 'fail' | 'skip';

export interface TesterRecord {
  test_name: string;
  mark: TestMark;
  duration_ms: number;
  determinism_classification: DeterminismClassification | null;
  failure_trace_ref?: string | null;
}

export interface VerificationGateStep {
  name: string;
  command: string;
  exit_code: number | null;
  passed: boolean;
  required: boolean;
  skipped: boolean;
}

export interface VerificationGate {
  passed: boolean;
  reason: string;
  evidence_available: boolean;
  steps: ReadonlyArray<VerificationGateStep>;
}

export interface TesterSummary {
  total_tests: number;
  passing_tests: number;
  failing_tests: number;
  unverified_tests: number;
  flaky_tests: number;
  tests_covering_findings: number;
  framework: string;
}

export interface TesterOutput {
  kind: 'tester';
  records: ReadonlyArray<TesterRecord>;
  summary?: TesterSummary | null;
  verification_gate?: VerificationGate | null;
}

export interface DocumenterDocDiff {
  doc_path: string;
  diff: ReadonlyArray<DiffLine>;
  stale_flags: ReadonlyArray<string>;
}

export interface DocumenterReadmeUpdate {
  path: string;
  change_type: 'created' | 'updated';
  sections_changed: ReadonlyArray<string>;
  content: string;
}

export interface DocumenterDocUpdate {
  path: string;
  change_type: 'created' | 'updated' | 'unchanged';
  content: string;
}

export interface DocumenterDocComment {
  file_path: string;
  symbol: string;
  comment_block: string;
  style?: string;
}

export interface DocumenterOutput {
  kind: 'documenter';
  doc_diffs: ReadonlyArray<DocumenterDocDiff>;
  readme_updates: ReadonlyArray<DocumenterReadmeUpdate>;
  doc_updates: ReadonlyArray<DocumenterDocUpdate>;
  doc_comments_added: ReadonlyArray<DocumenterDocComment>;
  warnings: ReadonlyArray<string>;
}

export type RoleOutput =
  | ExplorerOutput
  | ArchitectOutput
  | CoderOutput
  | ReviewerOutput
  | TesterOutput
  | DocumenterOutput;

export interface StageSubProgress {
  progress_pct: number;
  current_step: string;
  latency_so_far_ms: number;
}

export interface PartialFailureSummary {
  failed_branch_id: string;
  failure_reason: string;
  failed_at: string;
}

export interface BackEdgeInfo {
  from_stage: string;
  to_stage: string;
  attempt_number: number;
  max_attempts?: number;
  fired_at: string;
  feedback_summary: string;
}

export interface StageDetailDto {
  pipeline_run_id: string;
  stage_id: string;
  role: AgentRole;
  state: StageState;
  outcome: StageOutcome;
  started_at: string | null;
  completed_at: string | null;
  attempt_number: number;
  provenance: ArtefactProvenance;
  output: RoleOutput | null;
  sub_progress: StageSubProgress | null;
  back_edge: BackEdgeInfo | null;
  partial_failure: PartialFailureSummary | null;
  raw_output_artefact_id: string | null;
}


export interface PipelineRunCostDto {
  pipeline_run_id: string;
  input_tokens: number;
  output_tokens: number;
  reasoning_tokens: number;
  cached_tokens: number;
  cache_write_tokens: number;
  total_tokens: number;
  total_cost_usd_estimate: number;
}


export type FailureType =
  | 'timeout'
  | 'network_error'
  | 'rate_limited'
  | 'auth_error'
  | 'server_error_5xx'
  | 'client_error_4xx'
  | 'validation_error'
  | 'agentic_no_final_answer'
  | 'stopped_by_user'
  | 'unknown_error';

export const FAILURE_TYPE_LABELS: Record<FailureType, string> = {
  timeout: 'Timed out',
  network_error: 'Network error',
  rate_limited: 'Rate limit / quota exceeded',
  auth_error: 'Authentication error',
  server_error_5xx: 'Upstream server error',
  client_error_4xx: 'Request rejected',
  validation_error: 'Validation error',
  agentic_no_final_answer: 'Agent ended without an answer',
  stopped_by_user: 'Stopped by a person',
  unknown_error: 'Unknown error'
};

export function isOperatorDecision(value: string): boolean {
  return value === 'stopped_by_user';
}

export function failureTypeLabel(value: string): string {
  return (FAILURE_TYPE_LABELS as Record<string, string>)[value] ?? 'Unknown error';
}

export interface FailureRecordDto {
  pipeline_run_id: string;
  failed_at_stage: string;
  failed_at_agent_role: AgentRole;
  failure_type: FailureType;
  failure_cause: string;
  state_snapshot: Record<string, unknown>;
  recorded_at: string;
}


export type StallCause = 'stage_timeout' | 'pipeline_timeout' | 'agent_unresponsive';

export interface StallDetailDto {
  pipeline_run_id: string;
  stall_cause: StallCause;
  detected_at: string;
  stalled_stage: string;
  agent_id: string;
  no_progress_for_ms: number;
  last_event: string;
  last_event_at: string;
  pid_alive: boolean;
  socket_responsive: boolean;
}


export interface ApprovalRequestDto {
  approval_id: string;
  pipeline_run_id: string;
  paused_at_stage: string;
  requested_by_agent_role: AgentRole;
  reason: string;
  paused_at: string;
  gate_mode?: 'failure_escalation' | 'step_through';
  requested_by_user_id?: string;
  deliverability?: import('../components/DeliverabilityWarning').DeliverabilityPayload;
  resolvability?: import('../components/ResolvabilityWarning').ResolvabilityPayload;
  sla_minutes?: number;
  sla_deadline?: string;
  breach_notified?: boolean;
  tat_seconds?: number;
  can_decide?: boolean;
  decide_denied_reason?: string;
}

export type ApprovalDecision = 'approved' | 'denied';

export interface NotificationDto {
  notification_id: string;
  recipient_user_id: string;
  kind: 'approval.sla_breached';
  created_at: string;
  read_at?: string;
  title: string;
  body: string;
  pipeline_run_id?: string;
  stage_id?: string;
  stage_role?: string;
  project_id?: string;
  project_name?: string;
  project_slug?: string;
  approval_id?: string;
  approver_name?: string;
  approver_email?: string;
  sla_minutes?: number;
  elapsed_seconds?: number;
}

export interface NotificationListResponse {
  notifications: NotificationDto[];
  unread_count: number;
  total?: number;
  has_more?: boolean;
}

export interface NotificationListQueryArgs {
  onlyUnread?: boolean;
  page_size?: number;
  offset?: number;
}

export interface SlaReportRow {
  approver_user_id: string;
  approver_name: string;
  approver_email: string;
  project_id: string;
  project_name: string;
  avg_tat_seconds: number;
  decided_count: number;
  breach_count: number;
  project_breach_count: number;
  by_stage?: Record<string, number>;
  assigned_stages?: string[];
}

export interface SlaReportResponse {
  rows: SlaReportRow[];
  summary: {
    project_count: number;
    approver_count: number;
    avg_tat_seconds: number;
    total_breaches: number;
  };
}

export interface PendingApprovalItem {
  approval_id: string;
  pipeline_run_id: string;
  paused_at_stage: string;
  stage_role: string;
  project_id: string;
  project_name: string;
  project_slug?: string;
  paused_at: string;
  requested_by_agent_role: string;
  sla_minutes?: number;
  sla_deadline?: string;
  breach_notified: boolean;
  doubt_status?: DoubtStatus;
  doubt_id?: string;
}

export interface PendingApprovalsResponse {
  approvals: PendingApprovalItem[];
}

export interface ApprovalDecideRequest {
  approval_id: string;
  decision: ApprovalDecision;
  reason: string;
}

export interface ApprovalDecideResponse {
  approval_id: string;
  state: string;
  decided_at: string;
  decision_reason?: string;
}

export type DoubtStatus =
  | 'analysing'
  | 'verdict_pending'
  | 'resolved'
  | 'declined'
  | 'failed';

export type DoubtAction =
  | 'reroute'
  | 'decline_continue'
  | 'decline_fail'
  | 'supply_requirement';

export interface DoubtStageEvidence {
  stage: string;
  assessment: 'honoured' | 'failed' | 'not_applicable';
  what_it_produced: string;
  why: string;
  refs: string[];
}

export interface DoubtVerdictDto {
  outcome: 'not_in_requirements' | 'stage_at_fault' | 'no_issue_found';
  confidence: 'high' | 'medium' | 'low';
  summary: string;
  requirement_basis: {
    found_in_requirements: boolean;
    quotes: Array<{ source: string; ref: string; text: string }>;
  };
  culprit_stage: string | null;
  stage_evidence: DoubtStageEvidence[];
  stage_prompt: string | null;
  notes: string;
}

export interface DoubtDto {
  doubt_id: string;
  status: DoubtStatus;
  raised_at: string;
  raised_by_user_id: string;
  text: string;
  attachment_ref?: string;
  verdict?: DoubtVerdictDto;
  failure_reason?: string;
  decided_by_user_id?: string;
  decided_at?: string;
  action?: DoubtAction;
  permitted_actions?: DoubtAction[];
}

export interface RaiseDoubtRequest {
  approval_id: string;
  text: string;
  attachment_ref?: string;
}

export interface RaiseDoubtResponse {
  approval_id: string;
  doubt_id: string;
  status: DoubtStatus;
  raised_at: string;
}

export interface DoubtStatusResponse {
  approval_id: string;
  doubt: DoubtDto | null;
}

export interface DecideDoubtRequest {
  approval_id: string;
  doubt_id: string;
  action: DoubtAction;
  reason: string;
  requirement_text?: string;
}


export interface DecideDoubtResponse {
  approval_id: string;
  doubt_id: string;
  action: DoubtAction;
  status: DoubtStatus;
  decided_at?: string;
}


export interface ReplayRequest {
  pipeline_run_id: string;
  from_stage: string;
}

export interface ReplayResponse {
  pipeline_run_id: string;
  resumed_at_stage: string;
  resumed_at: string;
}


export type UserRole = 'admin' | 'approver' | 'task_submitter';

export interface CurrentUserDto {
  user_id: string;
  email: string;
  name?: string;
  role: UserRole;
  capabilities: ReadonlyArray<string>;
}

export interface AdminUserDto {
  user_id: string;
  email: string;
  system_role: UserRole;
  display_name: string | null;
  status: 'active' | 'disabled';
  last_login_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface AdminUsersListResponse {
  users: ReadonlyArray<AdminUserDto>;
}

export interface CreateUserRequest {
  email: string;
  password: string;
  role: UserRole;
  display_name?: string;
}

export interface SetUserRoleRequest {
  user_id: string;
  role: UserRole;
}


export interface RegisterRequest {
  name: string;
  email: string;
  password: string;
}

export interface PublicUserDto {
  user_id: string;
  email: string;
  name: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface SessionDto {
  access_token: string;
  refresh_token: string;
  expires_at: string;
  user: CurrentUserDto;
}

export interface PasswordResetRequestRequest {
  email: string;
}

export interface PasswordResetConfirmRequest {
  token: string;
  new_password: string;
}

export interface PasswordChangeRequest {
  current_password: string;
  new_password: string;
}

export interface PasswordChangeResponse {
  sessions_revoked: number;
}


export interface ProjectListItemDto {
  project_id: string;
  slug: string;
  name: string;
  member_role: UserRole;
  member_count: number;
  last_run_state: PipelineRunState | null;
  last_run_at: string | null;
  has_active_run: boolean;
  updated_at: string;
}

export interface ProjectListResponseDto {
  projects: ReadonlyArray<ProjectListItemDto>;
  total?: number;
  has_more?: boolean;
}

export interface ProjectListQueryArgs {
  page_size?: number;
  offset?: number;
  search?: string;
}



export interface ProjectDetailDto {
  project_id: string;
  slug: string;
  name: string;
  description: string;
  git_url: string | null;
  git_protocol: GitProtocol | null;
  git_username?: string;
  git_auth_mode?: GitAuthMode;
  default_branch?: string;
  tech_stack: { frontend: string[]; backend: string[]; database: string[] } | null;
  tech_stack_editable: boolean;
  member_role: UserRole;
}

export interface ProjectMemberDto {
  user_id: string;
  name: string | null;
  email: string | null;
  project_role: UserRole;
  stages?: string[];
  added_at: string;
}

export interface ProjectMembersResponseDto {
  members: ReadonlyArray<ProjectMemberDto>;
}

export interface AddMemberRequest {
  user_id?: string;
  email?: string;
  project_role?: UserRole;
  stages?: string[];
}

export interface ChangeMemberRoleRequest {
  project_role: UserRole;
}

export interface SetMemberStagesRequest {
  stages: string[];
}


export interface ProjectCreateRequest {
  name: string;
  description?: string;
  git_url?: string;
  git_protocol?: GitProtocol;
  git_username?: string;
  git_auth_mode?: GitAuthMode;
  default_branch?: string;
  credential_id?: string;
  inline_credential?: { credential_type: 'git_https_token'; plaintext: string };
  tech_stack?: { frontend: string[]; backend: string[]; database: string[] } | null;
}

export interface CredentialMaskDto {
  is_set: boolean;
  prefix?: string;
  last4?: string;
  length?: number;
  updated_at?: string;
}

export interface ProjectCreateResponse {
  project_id: string;
  slug: string;
}

export interface ProjectConfigPayload {
  agent_config: Record<
    string,
    {
      reasoning_effort: string;
      pause_after?: boolean;
      approval_sla_minutes?: number;
      model?: string;
    }
  >;
  retry_policy: Record<string, { max_attempts: number; timeout_seconds: number }>;
  max_conflict_retries?: number;
}


export type GitProtocol = 'ssh' | 'https';

export type GitAuthMode = 'basic' | 'bearer';

export interface GitTestConnectionRequest {
  git_url: string;
  protocol: GitProtocol;
  credential: string;
  git_username?: string;
  git_auth_mode?: GitAuthMode;
}

export type GitConnectionOutcome =
  | 'success'
  | 'auth_failed'
  | 'not_found'
  | 'network_error'
  | 'unsupported_protocol';

export interface GitTestConnectionResponse {
  outcome: GitConnectionOutcome;
  head_sha?: string;
  credential_id?: string;
  saved_credential_id?: string;
  is_empty?: boolean;
}

export interface ConventionFileDto {
  convention_id: string;
  file_name: string;
  convention_type: string;
  size_bytes: number;
  applies_to: ReadonlyArray<AgentRole>;
}

export interface ConventionListResponseDto {
  conventions: ReadonlyArray<ConventionFileDto>;
}


export interface PipelineRunCreateRequest {
  project_id: string;
  workflow_id: string;
  inputs: {
    user_task_description: string;
    [k: string]: unknown;
  };
}

export interface PipelineRunCreateResponse {
  pipeline_run_id: string;
}

export interface RequirementUploadResponse {
  upload_id: string;
  extracted_text: string;
  char_count: number;
}

export type RequirementUploadReason =
  | 'unsupported_file'
  | 'file_too_large'
  | 'extraction_failed'
  | 'password_protected'
  | 'macro_enabled'
  | 'corrupted_file';


export interface AuditLogQuery {
  project_id: string;
  event_type?: string;
  emitted_from?: string;
  emitted_to?: string;
  actor?: string;
  target_resource?: string;
  page_size?: number;
  cursor?: string;
}

export interface AuditLogEntryDto {
  event_id: string;
  emitted_at: string;
  event_type: string;
  actor: string;
  actor_role?: string | null;
  actor_email?: string | null;
  message?: string | null;
  target_resource: string;
  payload_summary: Record<string, unknown>;
}

export interface AuditLogResponseDto {
  events: ReadonlyArray<AuditLogEntryDto>;
  next_cursor: string | null;
  total?: number | null;
}


export interface RunSignalSummary {
  stalled: boolean;
  stall_cause: StallCause | null;
}

export interface ModelPriceDto {
  model_id: string;
  input_per_1k: number;
  output_per_1k: number;
  cached_per_1k?: number;
  cache_write_per_1k?: number;
  cached_inclusive_in_input?: boolean;
  rate_version: number;
  selectable?: boolean;
  label?: string;
  sort_order?: number;
  source?: string;
  updated_by?: string;
  updated_at?: string;
  effective_from?: string;
  expires_at?: string;
}

export interface SelectableModelDto {
  model_id: string;
  label: string;
}

export interface ModelPriceListDto {
  models: ReadonlyArray<ModelPriceDto>;
  selectable: ReadonlyArray<SelectableModelDto>;
  rate_version: number;
}

export interface RateDeltaDto {
  field: string;
  from: number | null;
  to: number | null;
}

export interface PerThousandRateDto {
  model: string;
  input_per_1k: number | null;
  cached_per_1k: number | null;
  cache_write_per_1k: number | null;
  output_per_1k: number | null;
}

export interface ModelPriceProposalDto {
  proposal_id: string;
  fetched_at: string;
  url: string;
  parse_ok: boolean;
  errors: ReadonlyArray<string>;
  warnings: ReadonlyArray<string>;
  diff: {
    changed: ReadonlyArray<{ model: string; deltas: ReadonlyArray<RateDeltaDto> }>;
    added: ReadonlyArray<PerThousandRateDto>;
    unchanged: ReadonlyArray<string>;
    removed: ReadonlyArray<{ model: string; reason: string }>;
  };
  unpriceable: ReadonlyArray<{ model: string; missing: ReadonlyArray<string> }>;
  dropped_non_api: ReadonlyArray<string>;
  not_dispatchable: ReadonlyArray<string>;
  filter_basis: string;
  api_list_error?: string;
  unpriced_in_use: ReadonlyArray<{ model: string; reason: string; risk: string }>;
}

export interface ModelPriceApplyResultDto {
  applied: ReadonlyArray<string>;
  skipped: ReadonlyArray<string>;
  rate_version: number;
  applied_by: string;
  applied_at: string;
}
